import type { DayLevel, HealthDay } from '@pa/api/clinic'
import { useEffect, useRef, useState } from 'react'
import { tooltipDate } from '../model/format'
import { LEVEL_LABEL, levelClass } from '../model/labels'
import { DayTooltip } from './DayTooltip'

/**
 * C-12 uptime bars (UserFlows C-12; the mockup's `UptimeBars`): one bar per day, oldest first, coloured by level
 * (grey for no data). Hovering a bar, or focusing it (one tab stop; arrow keys move between days), shows the day
 * tooltip.
 */
export function UptimeBars({ days, timeZone }: { days: readonly HealthDay[]; timeZone: string }) {
  const [tip, setTip] = useState<{ day: HealthDay; x: number; y: number } | null>(null)
  const [active, setActive] = useState(days.length - 1)
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const show = (d: HealthDay, el: HTMLElement) => {
    const r = el.getBoundingClientRect()
    setTip({ day: d, x: Math.min(Math.max(r.left + r.width / 2, 150), window.innerWidth - 150), y: r.top - 10 })
  }
  const hide = () => {
    setTip(null)
  }
  // The tooltip is placed for the layout it opened in; a resize (a tablet turned) moves the bars, so it closes.
  const open = tip !== null
  useEffect(() => {
    if (!open) return
    const close = () => {
      setTip(null)
    }
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('resize', close)
    }
  }, [open])
  return (
    <>
      <div className="uptime-bars" role="group" aria-label={`Daily uptime, ${String(days.length)} days`}>
        {days.map((d, i) => (
          <button
            key={d.date}
            ref={(el) => {
              refs.current[i] = el
            }}
            type="button"
            className={`ub ${levelClass(d.level)}`}
            aria-label={`${tooltipDate(d.date)}: ${LEVEL_LABEL[d.level]}`}
            tabIndex={i === active ? 0 : -1}
            onMouseEnter={(e) => {
              show(d, e.currentTarget)
            }}
            onMouseLeave={hide}
            onFocus={(e) => {
              setActive(i)
              show(d, e.currentTarget)
            }}
            onBlur={hide}
            onKeyDown={(e) => {
              const next = e.key === 'ArrowLeft' ? i - 1 : e.key === 'ArrowRight' ? i + 1 : undefined
              if (next === undefined || next < 0 || next >= days.length) return
              e.preventDefault()
              refs.current[next]?.focus()
            }}
          />
        ))}
      </div>
      {tip && <DayTooltip day={tip.day} timeZone={timeZone} x={tip.x} y={tip.y} />}
    </>
  )
}

/** S-07's compact strip of `overall` day levels (no tooltip). */
export function UptimeStrip({ days }: { days: readonly { date: string; level: DayLevel }[] }) {
  return (
    <div className="uptime-bars uptime-strip" role="img" aria-label={`${String(days.length)} days of uptime`}>
      {days.map((d) => (
        <span key={d.date} className={`ub ${levelClass(d.level)}`} />
      ))}
    </div>
  )
}
